import React, { useEffect, useState } from 'react';
import './Subscription.css';
import {StripeSubscription, cancelSubscription, getCurrentUserSub, getCurrentUser} from '../../../api/productService';
import { useConfirmationDialog } from '../../../hooks/useConfirmationDialog';
import ConfirmationDialog from '../../../funnel/ConfirmationDialog/ConfirmationDialog';
import { useToast } from '../../../hooks/useToast';
import CopyrightText from "../CopyrightText/CopyrightText";

const Subscription: React.FC = () => {
  const [subscription, setSubscription] = useState<StripeSubscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canceling, setCanceling] = useState(false);
  const {
    isOpen,
    config,
    primaryAction,
    secondaryAction,
    hideConfirmation,
    showCancelSubscription
  } = useConfirmationDialog();
  const { showError, showSubscriptionCanceled } = useToast();

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const userData = await getCurrentUser();
        const data = await getCurrentUserSub(userData.user.email);
        if (data.subscription && data.subscription.length > 0) {
          setSubscription(data.subscription[0]);
        }
      } catch (err) {
        setError('Failed to fetch subscription details.');
      } finally {
        setLoading(false);
      }
    };

    fetchSubscription();
  }, []);

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: currency.toUpperCase() }).format(amount / 100);
  };

  const getBillingPeriod = (recurring: any) => {
    if (recurring.interval === 'week' && recurring.interval_count === 12) {
      return '12 weeks';
    }
    if (recurring.interval === 'month') {
      return `${recurring.interval_count} month${recurring.interval_count > 1 ? 's' : ''}`;
    }
    return `${recurring.interval_count} ${recurring.interval}${recurring.interval_count > 1 ? 's' : ''}`;
  };

  const formatDate = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleDateString('en-US', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  const handleCancelSubscription = () => {
    showCancelSubscription(async () => {
      if (!subscription?.id) {
        showError('Unable to cancel subscription: Missing subscription ID');
        hideConfirmation();
        return;
      }

      setCanceling(true);
      try {
        await cancelSubscription(subscription.id);
        showSubscriptionCanceled();

        // Refresh subscription data or redirect to courses page
        setTimeout(() => {
          window.location.reload(); // Refresh to show updated subscription status
        }, 2000);
      } catch (error: any) {
        console.error('Error canceling subscription:', error);
        const errorMessage = error.response?.data?.error?.message ||
                            error.response?.data?.message ||
                            'Failed to cancel subscription. Please try again.';
        showError(errorMessage);
      } finally {
        setCanceling(false);
        hideConfirmation();
      }
    });
  };

  if (loading) {
    return <div className="center-content"></div>;
  }

  if (error) {
    return <div className="center-content">{error}</div>;
  }

  if (!subscription) {
    return <div className="center-content">No subscription found.</div>;
  }

  // Extract price information from subscription items
  const subscriptionItem = subscription.items.data[0];
  const price = subscriptionItem.price;

  // Get current period end for next payment date
  const nextPaymentDate = subscriptionItem.current_period_end;

  return (
    <div className="subscription-container">
      <main className="subscription-content">
        <h2>My plans</h2>
        <div className="plan-card">
          <h3>Subscriptions</h3>
          <div className="plan-details">
            <div className="detail-row">
              <div>Status:</div>
              <div style={{ textTransform: 'capitalize' }}>{subscription.status}</div>
            </div>
            <div className="detail-row">
              <div>Billing period:</div>
              <div>{getBillingPeriod(price.recurring)}</div>
            </div>
            <div className="detail-row">
              <div>Next payment date:</div>
              <div>{formatDate(nextPaymentDate)}</div>
            </div>
            <div className="detail-row">
              <div>Subscription price:</div>
              <div>{formatCurrency(price.unit_amount, price.currency)}</div>
            </div>
            <div className="detail-row total">
              <div>Subscription total:</div>
              <div>{formatCurrency(price.unit_amount, price.currency)}</div>
            </div>
          </div>
          <p className="expiration-date">
            {subscription.cancel_at_period_end
              ? `Will be canceled on ${formatDate(nextPaymentDate)}`
              : `Active until ${formatDate(nextPaymentDate)}`
            }
          </p>
          <button
            className="cancel-button"
            onClick={handleCancelSubscription}
            disabled={canceling || subscription.cancel_at_period_end}
          >
            {canceling ? 'Canceling...' :
             subscription.cancel_at_period_end ? 'Cancellation Scheduled' :
             'Cancel Subscription'}
          </button>
        </div>
        <CopyrightText />
      </main>
      {isOpen && config && (
        <ConfirmationDialog
          isOpen={isOpen}
          type={config.type || 'info'}
          title={config.title}
          message={config.message}
          primaryButtonText={config.primaryButtonText || 'Confirm'}
          secondaryButtonText={config.secondaryButtonText}
          showIcon={config.showIcon}
          onPrimaryAction={primaryAction || (() => {})}
          onSecondaryAction={secondaryAction || undefined}
          onClose={hideConfirmation}
        />
      )}
    </div>
  );
};

export default Subscription;
